import { useState, useEffect } from "react";
import {
    Paper,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    CircularProgress,
    Typography,
    Tooltip,
    InputAdornment,
    TableContainer,
    Fade,
    Chip,
    IconButton,
    Drawer,
    useMediaQuery,
    useTheme,
    Collapse
} from "@mui/material";

import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import { fetchData } from "../../../apiService";
import { useAuth } from "../../../AuthContext";
import { tokenIsExpired } from "../../../authUtils";

function DeliverySummaryReport() {
    const { isAuthenticated, companyId, branchId, token, logout, username } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [mounted, setMounted] = useState(false);
    const [statusFilter, setStatusFilter] = useState("All");
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
    const [expandedRows, setExpandedRows] = useState({});

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [companyDetails, setCompanyDetails] = useState(null);
    const [branchDetails, setBranchDetails] = useState(null);

    useEffect(() => {
        setMounted(true);
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }

        const today = new Date();
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        setFromDate(firstDayOfMonth.toISOString().split("T")[0]);
        setToDate(today.toISOString().split("T")[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (fromDate && toDate && companyId && branchId && token) {
            fetchTableData();
            fetchCompanyDetails();
            fetchBranchDetails();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromDate, toDate]);

    const fetchCompanyDetails = async () => {
        try {
            const response = await fetchData('CompanyApi', `/${companyId}`, token);
            if (response && response.responseCode === '1111') {
                const data = response.response_data || response.data || response;
                setCompanyDetails(Array.isArray(data) ? data[0] : data);
            }
        } catch (error) {
            console.error('Error fetching company details:', error);
        }
    };

    const fetchBranchDetails = async () => {
        try {
            const response = await fetchData('BranchApi', `/${branchId}`, token);
            if (response && response.responseCode === '1111') {
                const data = response.response_data || response.data || response;
                setBranchDetails(Array.isArray(data) ? data[0] : data);
            }
        } catch (error) {
            console.error('Error fetching branch details:', error);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        // If date is already in DD/MM/YYYY format, return as is
        if (dateString.includes('/')) return dateString;
        // Otherwise, format it
        return new Date(dateString).toLocaleDateString("en-GB");
    };

    const fetchTableData = async () => {
        if (!fromDate || !toDate) {
            return;
        }
        setIsLoading(true);

        setStatusFilter("All");
        setSearchQuery("");

        try {
            const response = await fetchData(
                "ReportApi",
                `/DeliverySummaryReport/${companyId}/${branchId}/${fromDate}/${toDate}`,
                token
            );

            if (response.responseCode === "1111") {
                const rawData = response.response_data || [];

                // Process data sequentially: delivery headers followed by their items
                const processedData = [];
                let currentDelivery = null;
                let currentItems = [];

                rawData.forEach((item) => {
                    // Check if this is a delivery header (has DeliveryCallId > 0)
                    if (item.DeliveryCallId && item.DeliveryCallId > 0 && item.DeliveryNo && item.DeliveryNo.trim() !== '') {
                        // If we have a previous delivery, save it with its items
                        if (currentDelivery) {
                            processedData.push({
                                ...currentDelivery,
                                items: currentItems
                            });
                        }

                        // Start a new delivery
                        currentDelivery = {
                            DeliveryNo: item.DeliveryNo,
                            BillDate: item.BillDate,
                            CustomerName: item.CustomerName,
                            ContactNumber1: item.ContactNumber1,
                            EventName: item.EventName,
                            ProgramDate: item.ProgramDate,
                            ExpectedTime: item.ExpectedTime,
                            EventDays: item.EventDays,
                            RentDays: item.RentDays,
                            DeliveryOutVehicleNo: item.DeliveryOutVehicleNo,
                            DeliveryOutVehicleName: item.DeliveryOutVehicleName,
                            DeliveryOutTime: item.DeliveryOutTime,
                            DeliveryInTime: item.DeliveryInTime,
                            DeliveryInVehicleNo: item.DeliveryInVehicleNo,
                            DeliveryInVehicleName: item.DeliveryInVehicleName,
                            Status: item.Status
                        };
                        currentItems = []; // Reset items for new delivery
                    }
                    // Check if this is an item (DeliveryCallId === 0 and has ItemName)
                    else if ((!item.DeliveryCallId || item.DeliveryCallId === 0) && item.ItemName && item.ItemName.trim() !== '') {
                        // Add item to current delivery's items
                        currentItems.push({
                            ItemName: item.ItemName,
                            DeliveryOutQuantity: item.DeliveryOutQuantity,
                            GoodQuantity: item.GoodQuantity,
                            DamagedQuantity: item.DamagedQuantity,
                            DeliveryInQuantity: item.DeliveryInQuantity
                        });
                    }
                });

                // Don't forget to add the last delivery
                if (currentDelivery) {
                    processedData.push({
                        ...currentDelivery,
                        items: currentItems
                    });
                }

                setDataItem(processedData);
            }
        } catch (error) {
            console.error("Error fetching delivery summary:", error);
            setDataItem([]);
        } finally {
            setIsLoading(false);
        }
    };

    const toggleRow = (deliveryNo) => {
        setExpandedRows(prev => ({
            ...prev,
            [deliveryNo]: !prev[deliveryNo]
        }));
    };

    const filteredData = dataItem
        .filter((item) => {
            if (statusFilter === "Completed") {
                return item.Status === "Delivery In Completed";
            } else if (statusFilter === "Pending") {
                return item.Status !== "Delivery In Completed";
            }
            return true;
        })
        .filter((item) =>
            Object.values(item).some((value) =>
                String(value).toLowerCase().includes(searchQuery.toLowerCase())
            )
        );

    const processedData = filteredData.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const handleExportExcel = async () => {
        if (filteredData.length === 0) {
            alert("No data to export");
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Delivery Summary");

        // Add Report Title
        const titleRow = worksheet.addRow(['DELIVERY SUMMARY REPORT']);
        titleRow.font = { bold: true, size: 16, color: { argb: "FF667eea" } };
        titleRow.alignment = { horizontal: 'center', vertical: 'middle' };
        titleRow.height = 30;
        worksheet.mergeCells(1, 1, 1, 8); // Merge cells for title
        
        worksheet.addRow([]); // Empty row for spacing

        const companyName = companyDetails?.CompanyName || companyDetails?.Name || 'N/A';
        const branchName = branchDetails?.BranchName || branchDetails?.Name || 'N/A';

        const companyBranchRow = worksheet.addRow(['Company:', companyName, '|', 'Branch:', branchName]);
        companyBranchRow.font = { bold: true, size: 12 };
        companyBranchRow.getCell(1).font = { bold: true };
        companyBranchRow.getCell(4).font = { bold: true };

        const currentDateTime = new Date().toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
        const infoRow = worksheet.addRow([
            'Date Range:',
            `${formatDate(fromDate)} to ${formatDate(toDate)}`,
            '|',
            'Generated By:',
            username || 'N/A',
            '|',
            'Generated Time:',
            currentDateTime
        ]);
        infoRow.font = { bold: true, size: 12 };
        infoRow.getCell(1).font = { bold: true };
        infoRow.getCell(4).font = { bold: true };
        infoRow.getCell(7).font = { bold: true };

        worksheet.addRow([]);

        filteredData.forEach((delivery, deliveryIndex) => {
            // Add delivery header row - split into multiple columns for better layout
            const headerRow = worksheet.addRow([
                'Delivery #:', delivery.DeliveryNo,
                'Date:', formatDate(delivery.BillDate),
                'Customer:', delivery.CustomerName,
                'Contact:', delivery.ContactNumber1
            ]);
            headerRow.font = { bold: true, size: 11 };
            headerRow.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFD1D5DB" }
            };
            headerRow.alignment = { vertical: 'middle' };
            headerRow.height = 20;

            // Second header row with event and delivery info
            const eventRow = worksheet.addRow([
                'Event:', delivery.EventName,
                'Program Date:', delivery.ProgramDate,
                'Expected Time:', delivery.ExpectedTime,
                'Event Days:', delivery.EventDays,
                'Rent Days:', delivery.RentDays
            ]);
            eventRow.font = { bold: true, size: 10 };
            eventRow.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFD1D5DB" }
            };
            eventRow.alignment = { vertical: 'middle' };

            // Third header row with delivery out/in info
            const deliveryInfoRow = worksheet.addRow([
                'Delivery Out:', `${delivery.DeliveryOutVehicleNo} - ${delivery.DeliveryOutVehicleName}`,
                'Time:', delivery.DeliveryOutTime,
                'Delivery In:', `${delivery.DeliveryInVehicleNo} - ${delivery.DeliveryInVehicleName}`,
                'Time:', delivery.DeliveryInTime,
                'Status:', delivery.Status
            ]);
            deliveryInfoRow.font = { bold: true, size: 10 };
            deliveryInfoRow.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFD1D5DB" }
            };
            deliveryInfoRow.alignment = { vertical: 'middle' };

            // Add items table header
            const itemsHeaderRow = worksheet.addRow([
                "Item Name",
                "Out Qty",
                "Good Qty",
                "Damaged Qty",
                "In Qty"
            ]);
            itemsHeaderRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
            itemsHeaderRow.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FF667eea" }
            };
            itemsHeaderRow.alignment = { vertical: 'middle', horizontal: 'center' };
            itemsHeaderRow.height = 18;

            // Add items for this delivery
            if (delivery.items && delivery.items.length > 0) {
                delivery.items.forEach((item, itemIndex) => {
                    const itemRow = worksheet.addRow([
                        item.ItemName,
                        item.DeliveryOutQuantity,
                        item.GoodQuantity,
                        item.DamagedQuantity,
                        item.DeliveryInQuantity
                    ]);
                    
                    // Alternate row colors for items
                    if (itemIndex % 2 === 0) {
                        itemRow.fill = {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { argb: "FFF9FAFB" }
                        };
                    }
                    
                    // Right align quantity columns
                    itemRow.getCell(2).alignment = { horizontal: 'right' };
                    itemRow.getCell(3).alignment = { horizontal: 'right' };
                    itemRow.getCell(4).alignment = { horizontal: 'right' };
                    itemRow.getCell(5).alignment = { horizontal: 'right' };
                    
                    // Highlight damaged quantity if > 0
                    if (item.DamagedQuantity > 0) {
                        itemRow.getCell(4).fill = {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { argb: "FFFF0000" }
                        };
                        itemRow.getCell(4).font = { color: { argb: "FFFFFFFF" }, bold: true };
                    }
                    
                    // Highlight In Qty based on comparison with Good Qty
                    if (item.DeliveryInQuantity > 0) {
                        if (item.DeliveryInQuantity === item.GoodQuantity) {
                            itemRow.getCell(5).fill = {
                                type: "pattern",
                                pattern: "solid",
                                fgColor: { argb: "FF10B981" }
                            };
                            itemRow.getCell(5).font = { color: { argb: "FFFFFFFF" }, bold: true };
                        } else {
                            itemRow.getCell(5).fill = {
                                type: "pattern",
                                pattern: "solid",
                                fgColor: { argb: "FFF59E0B" }
                            };
                            itemRow.getCell(5).font = { color: { argb: "FFFFFFFF" }, bold: true };
                        }
                    }
                });
            }

            // Add spacing between deliveries
            worksheet.addRow([]);
        });

        worksheet.columns.forEach((col) => {
            col.width = 20;
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const fileName = `Delivery_Summary_${fromDate}_to_${toDate}.xlsx`;
        saveAs(new Blob([buffer]), fileName);
    };

    if (!mounted) return null;

    return (
        <Fade in={mounted} timeout={800}>
            <div style={{
                minWidth: '100%',
                background: '#f5f7fa',
                padding: '10px',
                position: 'relative'
            }}>
                <div style={{
                    width: '100%',
                    margin: '0 auto',
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    padding: '12px',
                    position: 'relative',
                    color: 'black',
                    fontFamily: 'Times New Romans',
                }}>
                    <div style={{
                        marginBottom: '12px',
                        paddingBottom: '8px',
                        borderBottom: '1px solid #f0f0f0'
                    }}>
                        <h4 style={{
                            fontSize: '17px',
                            fontWeight: '700',
                            margin: 0,
                            letterSpacing: '-0.3px',
                            fontFamily: 'Times New Romans',
                            textTransform: 'uppercase',
                            color: 'black'
                        }}>
                            Delivery Summary Report
                        </h4>
                    </div>

                    {!isMobile ? (
                        <div style={{
                            marginBottom: '12px',
                            overflowX: 'auto',
                            WebkitOverflowScrolling: 'touch',
                        }}>
                            <div style={{
                                display: 'flex',
                                gap: '8px',
                                flexWrap: 'nowrap',
                                minWidth: 'fit-content',
                                alignItems: 'flex-end'
                            }}>
                                <div style={{ flexShrink: 0 }}>
                                    <div style={{ fontSize: '10px', color: '#666', marginBottom: '2px', paddingLeft: '4px' }}>From Date</div>
                                    <TextField
                                        type="date"
                                        value={fromDate}
                                        onChange={(e) => setFromDate(e.target.value)}
                                        size="small"
                                        sx={{
                                            width: '140px',
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                backgroundColor: '#f8f9fa',
                                                fontSize: '11px',
                                                height: '28px',
                                                '&:hover fieldset': { borderColor: '#667eea' },
                                                '&.Mui-focused fieldset': { borderColor: '#667eea' },
                                            },
                                        }}
                                    />
                                </div>

                                <div style={{ flexShrink: 0 }}>
                                    <div style={{ fontSize: '10px', color: '#666', marginBottom: '2px', paddingLeft: '4px' }}>To Date</div>
                                    <TextField
                                        type="date"
                                        value={toDate}
                                        onChange={(e) => setToDate(e.target.value)}
                                        size="small"
                                        sx={{
                                            width: '140px',
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                backgroundColor: '#f8f9fa',
                                                fontSize: '11px',
                                                height: '28px',
                                                '&:hover fieldset': { borderColor: '#667eea' },
                                                '&.Mui-focused fieldset': { borderColor: '#667eea' },
                                            },
                                        }}
                                    />
                                </div>

                                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                                    <Button
                                        variant={statusFilter === "All" ? "contained" : "outlined"}
                                        onClick={() => setStatusFilter("All")}
                                        style={{
                                            background: statusFilter === "All" ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                                            color: statusFilter === "All" ? 'white' : '#667eea',
                                            borderColor: '#667eea',
                                            padding: '4px 12px',
                                            borderRadius: '6px',
                                            fontSize: '11px',
                                            fontWeight: '600',
                                            height: '28px',
                                            textTransform: 'none',
                                            minWidth: '60px'
                                        }}
                                    >
                                        All
                                    </Button>
                                    <Button
                                        variant={statusFilter === "Completed" ? "contained" : "outlined"}
                                        onClick={() => setStatusFilter("Completed")}
                                        style={{
                                            background: statusFilter === "Completed" ? 'linear-gradient(135deg, #10b981, #059669)' : 'transparent',
                                            color: statusFilter === "Completed" ? 'white' : '#10b981',
                                            borderColor: '#10b981',
                                            padding: '4px 12px',
                                            borderRadius: '6px',
                                            fontSize: '11px',
                                            fontWeight: '600',
                                            height: '28px',
                                            textTransform: 'none',
                                            minWidth: '90px'
                                        }}
                                    >
                                        Completed
                                    </Button>
                                    <Button
                                        variant={statusFilter === "Pending" ? "contained" : "outlined"}
                                        onClick={() => setStatusFilter("Pending")}
                                        style={{
                                            background: statusFilter === "Pending" ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'transparent',
                                            color: statusFilter === "Pending" ? 'white' : '#f59e0b',
                                            borderColor: '#f59e0b',
                                            padding: '4px 12px',
                                            borderRadius: '6px',
                                            fontSize: '11px',
                                            fontWeight: '600',
                                            height: '28px',
                                            textTransform: 'none',
                                            minWidth: '80px'
                                        }}
                                    >
                                        Pending
                                    </Button>
                                </div>

                                <TextField
                                    placeholder="Search deliveries..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    size="small"
                                    sx={{
                                        flexGrow: 1,
                                        minWidth: '200px',
                                        flexShrink: 0,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            backgroundColor: '#f8f9fa',
                                            fontSize: '11px',
                                            height: '28px',
                                            '&:hover fieldset': { borderColor: '#667eea' },
                                            '&.Mui-focused fieldset': { borderColor: '#667eea' },
                                        },
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon style={{ color: '#667eea', fontSize: '14px' }} />
                                            </InputAdornment>
                                        )
                                    }}
                                />

                                <Button
                                    variant="contained"
                                    onClick={fetchTableData}
                                    disabled={isLoading}
                                    startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon style={{ fontSize: '14px' }} />}
                                    style={{
                                        background: 'linear-gradient(135deg, #11998e 0%, #072412ff 100%)',
                                        color: 'white',
                                        padding: '4px 12px',
                                        borderRadius: '6px',
                                        fontSize: '11px',
                                        fontWeight: '600',
                                        height: '28px',
                                        textTransform: 'none',
                                        flexShrink: 0
                                    }}
                                >
                                    {isLoading ? "Loading..." : "Refresh"}
                                </Button>

                                <Button
                                    variant="contained"
                                    onClick={handleExportExcel}
                                    disabled={filteredData.length === 0}
                                    startIcon={<FileDownloadIcon style={{ fontSize: '14px' }} />}
                                    style={{
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        color: 'white',
                                        padding: '4px 12px',
                                        borderRadius: '6px',
                                        fontSize: '11px',
                                        fontWeight: '600',
                                        height: '28px',
                                        textTransform: 'none',
                                        flexShrink: 0
                                    }}
                                >
                                    Export Excel
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div style={{ marginBottom: '12px', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', minWidth: 'fit-content' }}>
                                <TextField type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} size="small"
                                    sx={{ width: '130px', flexShrink: 0, '& .MuiOutlinedInput-root': { borderRadius: '6px', backgroundColor: '#f8f9fa', fontSize: '11px', height: '32px' } }} />
                                <TextField type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} size="small"
                                    sx={{ width: '130px', flexShrink: 0, '& .MuiOutlinedInput-root': { borderRadius: '6px', backgroundColor: '#f8f9fa', fontSize: '11px', height: '32px' } }} />
                                <Tooltip title="Refresh"><IconButton onClick={fetchTableData} disabled={isLoading}
                                    sx={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: 'white', width: '36px', height: '32px', borderRadius: '6px', flexShrink: 0 }}>
                                    {isLoading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}</IconButton></Tooltip>
                                <Tooltip title="Export"><IconButton onClick={handleExportExcel} disabled={filteredData.length === 0}
                                    sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', width: '36px', height: '32px', borderRadius: '6px', flexShrink: 0 }}>
                                    <FileDownloadIcon /></IconButton></Tooltip>
                                <Tooltip title="Filter & Search"><IconButton onClick={() => setFilterDrawerOpen(true)}
                                    sx={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: 'white', width: '36px', height: '32px', borderRadius: '6px', flexShrink: 0 }}>
                                    <FilterListIcon /></IconButton></Tooltip>
                            </div>
                        </div>
                    )}

                    <Drawer anchor="right" open={filterDrawerOpen} onClose={() => setFilterDrawerOpen(false)}>
                        <div style={{ width: '280px', padding: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                <Typography variant="h6" style={{ fontWeight: '600' }}>Filters</Typography>
                                <IconButton onClick={() => setFilterDrawerOpen(false)} size="small"><CloseIcon /></IconButton>
                            </div>
                            <div style={{ marginBottom: '20px' }}>
                                <Typography variant="subtitle2" style={{ marginBottom: '10px', fontWeight: '600' }}>Status</Typography>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <Button fullWidth variant={statusFilter === "All" ? "contained" : "outlined"} onClick={() => setStatusFilter("All")}
                                        style={{ background: statusFilter === "All" ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent', color: statusFilter === "All" ? 'white' : '#667eea', borderColor: '#667eea', textTransform: 'none' }}>All</Button>
                                    <Button fullWidth variant={statusFilter === "Completed" ? "contained" : "outlined"} onClick={() => setStatusFilter("Completed")}
                                        style={{ background: statusFilter === "Completed" ? 'linear-gradient(135deg, #10b981, #059669)' : 'transparent', color: statusFilter === "Completed" ? 'white' : '#10b981', borderColor: '#10b981', textTransform: 'none' }}>Completed</Button>
                                    <Button fullWidth variant={statusFilter === "Pending" ? "contained" : "outlined"} onClick={() => setStatusFilter("Pending")}
                                        style={{ background: statusFilter === "Pending" ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'transparent', color: statusFilter === "Pending" ? 'white' : '#f59e0b', borderColor: '#f59e0b', textTransform: 'none' }}>Pending</Button>
                                </div>
                            </div>
                            <div>
                                <Typography variant="subtitle2" style={{ marginBottom: '10px', fontWeight: '600' }}>Search</Typography>
                                <TextField fullWidth placeholder="Search deliveries..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} size="small"
                                    InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon style={{ color: '#667eea' }} /></InputAdornment>) }} />
                            </div>
                        </div>
                    </Drawer>

                    {/* Desktop Table View */}
                    {!isMobile && (
                    <div style={{ marginBottom: '10px' }}>
                        <TableContainer component={Paper} style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)', borderRadius: '10px', overflow: 'hidden' }}>
                            <Table sx={{ minWidth: 650 }}>
                                <TableHead>
                                    <TableRow style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                                        <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', padding: '4px 6px' }}>Delivery #</TableCell>
                                        <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', padding: '4px 6px' }}>Date</TableCell>
                                        <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', padding: '4px 6px' }}>Customer</TableCell>
                                        <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', padding: '4px 6px' }}>Event</TableCell>
                                        <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', padding: '4px 6px' }}>Days</TableCell>
                                        <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', padding: '4px 6px' }}>Delivery Out</TableCell>
                                        <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', padding: '4px 6px' }}>Time</TableCell>
                                        <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', padding: '4px 6px' }}>Delivery In</TableCell>
                                        <TableCell style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', padding: '4px 6px' }}>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {isLoading ? (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                                                <CircularProgress size={40} sx={{ color: '#667eea' }} />
                                                <Typography sx={{ mt: 2, color: '#64748b' }}>Loading deliveries...</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : processedData.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                                                <Typography color="text.secondary" style={{ fontSize: '12px' }}>No deliveries found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        processedData.map((row, index) => (
                                            <>
                                                <TableRow key={row.DeliveryNo + index} style={{ background: index % 2 === 0 ? '#ffffff' : '#f8f9fa', borderBottom: '2px solid #f0f0f0' }}>
                                                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: '600' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <IconButton size="small" onClick={() => toggleRow(row.DeliveryNo)} style={{ padding: '2px' }}>
                                                                {expandedRows[row.DeliveryNo] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                            </IconButton>
                                                            {row.DeliveryNo}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{formatDate(row.BillDate)}</TableCell>
                                                    <TableCell style={{ fontSize: '11px', padding: '8px 12px', color: '#2d3748' }}>
                                                        <div><strong>Customer:</strong> {row.CustomerName}</div>
                                                        <div><strong>Contact:</strong> {row.ContactNumber1}</div>
                                                    </TableCell>
                                                    <TableCell style={{ fontSize: '11px', padding: '8px 12px', color: '#2d3748' }}>
                                                        <div><strong>Event:</strong> {row.EventName}</div>
                                                        <div><strong>Program Date:</strong> {row.ProgramDate}</div>
                                                        <div><strong>Expected Time:</strong> {row.ExpectedTime}</div>
                                                    </TableCell>
                                                    <TableCell style={{ fontSize: '11px', padding: '8px 12px', color: '#2d3748' }}>
                                                        <div><strong>Event Days:</strong> {row.EventDays}</div>
                                                        <div><strong>Rent Days:</strong> {row.RentDays}</div>
                                                    </TableCell>
                                                    <TableCell style={{ fontSize: '11px', padding: '8px 12px', color: '#2d3748' }}>
                                                        <div><strong>Vehicle:</strong> {row.DeliveryOutVehicleNo}</div>
                                                        <div><strong>V.Name:</strong> {row.DeliveryOutVehicleName}</div>
                                                        <div><strong>Time:</strong> {row.DeliveryOutTime}</div>
                                                    </TableCell>
                                                    <TableCell style={{ fontSize: '11px', padding: '8px 12px', color: '#2d3748' }}>
                                                        <div><strong>Expected Time:</strong> {row.ExpectedTime}</div>
                                                    </TableCell>
                                                    <TableCell style={{ fontSize: '11px', padding: '8px 12px', color: '#2d3748' }}>
                                                        <div><strong>Vehicle:</strong> {row.DeliveryInVehicleNo}</div>
                                                        <div><strong>V.Name:</strong> {row.DeliveryInVehicleName}</div>
                                                        <div><strong>Time:</strong> {row.DeliveryInTime}</div>
                                                    </TableCell>
                                                    <TableCell style={{ fontSize: '12px', padding: '8px 12px' }}>
                                                        {row.Status === "Delivery In Completed" ? (
                                                            <Chip icon={<CheckCircleIcon style={{ color: '#ffffff', fontSize: '14px' }} />} label="COMPLETED"
                                                                sx={{
                                                                    background: 'linear-gradient(145deg, #10b981, #059669)', color: '#ffffff', fontWeight: '700', fontSize: '9px', height: '28px', borderRadius: '20px',
                                                                    boxShadow: '0 4px 8px rgba(16, 185, 129, 0.3)', '& .MuiChip-label': { padding: '0 12px', color: '#ffffff' }
                                                                }} />
                                                        ) : (
                                                            <Chip icon={<PendingIcon style={{ color: '#ffffff', fontSize: '14px' }} />} label="PENDING"
                                                                sx={{
                                                                    background: 'linear-gradient(145deg, #f59e0b, #d97706)', color: '#ffffff', fontWeight: '700', fontSize: '9px', height: '28px', borderRadius: '20px',
                                                                    boxShadow: '0 4px 8px rgba(245, 158, 11, 0.3)', '& .MuiChip-label': { padding: '0 12px', color: '#ffffff' }
                                                                }} />
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                                                        <Collapse in={expandedRows[row.DeliveryNo]} timeout="auto" unmountOnExit>
                                                            <div style={{ margin: '10px 0', padding: '10px', background: '#f8f9fa', borderRadius: '8px' }}>
                                                                <Table size="small">
                                                                    <TableHead>
                                                                        <TableRow style={{ background: '#e5e7eb' }}>
                                                                            <TableCell style={{ fontWeight: '600', fontSize: '10px' }}>Item Name</TableCell>
                                                                            <TableCell align="right" style={{ fontWeight: '600', fontSize: '10px' }}>Out Qty</TableCell>
                                                                            <TableCell align="right" style={{ fontWeight: '600', fontSize: '10px' }}>Good Qty</TableCell>
                                                                            <TableCell align="right" style={{ fontWeight: '600', fontSize: '10px' }}>Damaged Qty</TableCell>
                                                                            <TableCell align="right" style={{ fontWeight: '600', fontSize: '10px' }}>In Qty</TableCell>
                                                                        </TableRow>
                                                                    </TableHead>
                                                                    <TableBody>
                                                                        {row.items && row.items.map((item, itemIndex) => (
                                                                            <TableRow key={itemIndex}>
                                                                                <TableCell style={{ fontSize: '11px' }}>{item.ItemName}</TableCell>
                                                                                <TableCell align="right" style={{ fontSize: '11px' }}>{item.DeliveryOutQuantity}</TableCell>
                                                                                <TableCell align="right" style={{ fontSize: '11px' }}>{item.GoodQuantity}</TableCell>
                                                                                <TableCell align="right" style={{ fontSize: '11px' }}>
                                                                                    {item.DamagedQuantity > 0 ? (
                                                                                        <span style={{
                                                                                            color: 'white',
                                                                                            background: 'red',
                                                                                            borderRadius: '12px',
                                                                                            padding: '2px 8px',
                                                                                            fontWeight: '600',
                                                                                            display: 'inline-block'
                                                                                        }}>{item.DamagedQuantity}</span>
                                                                                    ) : (
                                                                                        item.DamagedQuantity
                                                                                    )}
                                                                                </TableCell>
                                                                                <TableCell align="right" style={{ fontSize: '11px' }}>
                                                                                    {item.DeliveryInQuantity > 0 ? (
                                                                                        <span style={{
                                                                                            color: 'white',
                                                                                            background: item.DeliveryInQuantity === item.DeliveryOutQuantity ? 'green' : '#f59e0b',
                                                                                            borderRadius: '12px',
                                                                                            padding: '2px 8px',
                                                                                            fontWeight: '600',
                                                                                            display: 'inline-block'
                                                                                        }}>{item.DeliveryInQuantity}</span>
                                                                                    ) : (
                                                                                        item.DeliveryInQuantity
                                                                                    )}
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        ))}
                                                                    </TableBody>
                                                                </Table>
                                                            </div>
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            </>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>
                    )}

                    {/* Mobile Card View */}
                    {isMobile && (
                        <div style={{ marginBottom: '10px' }}>
                            {isLoading ? (
                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                    <CircularProgress size={40} sx={{ color: '#667eea' }} />
                                    <Typography sx={{ mt: 2, color: '#64748b' }}>Loading deliveries...</Typography>
                                </div>
                            ) : processedData.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                    <Typography color="text.secondary" style={{ fontSize: '12px' }}>No deliveries found</Typography>
                                </div>
                            ) : (
                                processedData.map((row, index) => (
                                    <div key={row.DeliveryNo + index} style={{ 
                                        background: 'white', 
                                        borderRadius: '8px', 
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)', 
                                        marginBottom: '10px',
                                        padding: '12px',
                                        border: '1px solid #e5e7eb'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <IconButton size="small" onClick={() => toggleRow(row.DeliveryNo)} style={{ padding: '2px' }}>
                                                    {expandedRows[row.DeliveryNo] ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                                                </IconButton>
                                                <span style={{ fontWeight: '700', fontSize: '13px', color: '#667eea' }}>{row.DeliveryNo}</span>
                                            </div>
                                            {row.Status === "Delivery In Completed" ? (
                                                <Chip icon={<CheckCircleIcon style={{ color: '#ffffff', fontSize: '12px' }} />} label="COMPLETED"
                                                    sx={{ background: 'linear-gradient(145deg, #10b981, #059669)', color: '#ffffff', fontWeight: '700', fontSize: '8px', height: '22px', borderRadius: '15px',
                                                        '& .MuiChip-label': { padding: '0 8px', color: '#ffffff' } }} />
                                            ) : (
                                                <Chip icon={<PendingIcon style={{ color: '#ffffff', fontSize: '12px' }} />} label="PENDING"
                                                    sx={{ background: 'linear-gradient(145deg, #f59e0b, #d97706)', color: '#ffffff', fontWeight: '700', fontSize: '8px', height: '22px', borderRadius: '15px',
                                                        '& .MuiChip-label': { padding: '0 8px', color: '#ffffff' } }} />
                                            )}
                                        </div>
                                        
                                        <div style={{ fontSize: '10px', color: '#64748b', lineHeight: '1.6' }}>
                                            <div><strong>Date:</strong> {formatDate(row.BillDate)} | <strong>Customer:</strong> {row.CustomerName}</div>
                                            <div><strong>Event:</strong> {row.EventName} | <strong>Program:</strong> {row.ProgramDate}</div>
                                            <div><strong>Expected Time:</strong> {row.ExpectedTime} | <strong>Days:</strong> E:{row.EventDays} R:{row.RentDays}</div>
                                            <div><strong>Out:</strong> {row.DeliveryOutVehicleNo} @ {row.DeliveryOutTime} | <strong>In:</strong> {row.DeliveryInVehicleNo} @ {row.DeliveryInTime}</div>
                                        </div>

                                        <Collapse in={expandedRows[row.DeliveryNo]} timeout="auto" unmountOnExit>
                                            <div style={{ marginTop: '10px', padding: '8px', background: '#f8f9fa', borderRadius: '6px' }}>
                                                <Typography variant="subtitle2" style={{ fontWeight: '600', fontSize: '11px', marginBottom: '6px' }}>Items</Typography>
                                                {row.items && row.items.map((item, itemIndex) => (
                                                    <div key={itemIndex} style={{ 
                                                        fontSize: '10px', 
                                                        padding: '8px', 
                                                        background: 'white', 
                                                        borderRadius: '6px', 
                                                        marginBottom: '6px',
                                                        border: '1px solid #e5e7eb'
                                                    }}>
                                                        <div style={{ fontWeight: '600', marginBottom: '6px', color: '#2d3748' }}>{item.ItemName}</div>
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', fontSize: '9px' }}>
                                                            <span style={{ background: '#e5e7eb', padding: '2px 6px', borderRadius: '4px' }}>Out: {item.DeliveryOutQuantity}</span>
                                                            <span style={{ background: '#e5e7eb', padding: '2px 6px', borderRadius: '4px' }}>Good: {item.GoodQuantity}</span>
                                                            {item.DamagedQuantity > 0 && (
                                                                <span style={{ 
                                                                    color: 'white', 
                                                                    background: 'red', 
                                                                    borderRadius: '8px',
                                                                    padding: '2px 6px',
                                                                    fontWeight: '600'
                                                                }}>Dmg: {item.DamagedQuantity}</span>
                                                            )}
                                                            {item.DeliveryInQuantity > 0 && (
                                                                <span style={{ 
                                                                    color: 'white', 
                                                                    background: item.DeliveryInQuantity === item.GoodQuantity ? 'green' : '#f59e0b', 
                                                                    borderRadius: '8px',
                                                                    padding: '2px 6px',
                                                                    fontWeight: '600'
                                                                }}>In: {item.DeliveryInQuantity}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </Collapse>
                                    </div>
                                ))
                            )}
                        </div>
                    )}

                    <TablePagination component="div" count={filteredData.length} page={page} onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        sx={{ '& .MuiTablePagination-toolbar': { fontSize: '12px' }, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '12px', fontWeight: 600 } }} />
                </div>
            </div>
        </Fade>
    );
}

export default DeliverySummaryReport;
